import { createContext, useContext, type FC, type ReactNode } from 'react';
import type { ChatMessage } from '../types/chat.types';

/**
 * Stable conversation context - only updates when messages are complete.
 * NEVER contains streaming content - only final, completed messages.
 * Updates only when a message streaming is finished.
 *
 * This context is designed for high-frequency performance optimization:
 * - Contains only completed, stable message data
 * - Does NOT update during streaming (eliminates cascade re-renders)
 * - Consumed primarily by: MessageList component
 */
interface ChatConversationContextValue {
  /**
   * Array of completed chat messages only.
   * NEVER contains streaming content - only final, completed messages.
   * Updates only when a message streaming is finished.
   */
  messages: ChatMessage[];

  /**
   * Whether there is an active conversation.
   * Based on whether there are any messages in the conversation.
   */
  isConversationActive: boolean;

  /**
   * Unique identifier for the current conversation session.
   */
  conversationId: string;
}

const ChatConversationContext = createContext<
  ChatConversationContextValue | undefined
>(undefined);

interface ChatConversationProviderProps {
  children: ReactNode;
  messages: ChatMessage[];
  conversationId: string;
}

/**
 * Provider for stable conversation data.
 * This context only updates when completed messages are added/removed.
 * It never updates during streaming to prevent unnecessary re-renders.
 */
export const ChatConversationProvider: FC<ChatConversationProviderProps> = ({
  children,
  messages,
  conversationId,
}) => {
  const contextValue: ChatConversationContextValue = {
    messages,
    isConversationActive: messages.length > 0,
    conversationId,
  };

  return (
    <ChatConversationContext.Provider value={contextValue}>
      {children}
    </ChatConversationContext.Provider>
  );
};

/**
 * Hook to access stable conversation data.
 * Components using this hook will only re-render when completed messages change,
 * NOT during streaming updates.
 */
export const useChatConversation = (): ChatConversationContextValue => {
  const context = useContext(ChatConversationContext);
  if (context === undefined) {
    throw new Error(
      'useChatConversation must be used within a ChatConversationProvider',
    );
  }
  return context;
};
